/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row, Space } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type AdyenPaymentModuleSpecsFragment } from '../../../../api/fragments/AdyenPaymentModuleSpecs';
import { type AdyenPaymentSettingsSpecFragment } from '../../../../api/fragments/AdyenPaymentSettingsSpec';
import {
    UpdateAdyenPaymentSettingsDocument,
    type UpdateAdyenPaymentSettingsMutation,
    type UpdateAdyenPaymentSettingsMutationVariables,
} from '../../../../api/mutations/updateAdyenPaymentSettings';
import { type AdyenPaymentSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputFieldArray from '../../../../components/fields/InputFieldArray';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import useEnvironmentOptions from '../modules/implementations/shared/useEnvironmentOptions';
import validator from './validators';

export type FormValues = AdyenPaymentSettingsInput;

export type UpdateAdyenPaymentSettingInnerPageProps = {
    module: AdyenPaymentModuleSpecsFragment;
    paymentSettings: AdyenPaymentSettingsSpecFragment;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const UpdateAdyenPaymentSettingInnerPage = ({ module, paymentSettings }: UpdateAdyenPaymentSettingInnerPageProps) => {
    const moduleId = module.id;
    const paymentSettingsId = paymentSettings.id;

    const { t } = useTranslation(['adyenPaymentModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const initialValues = useMemo(
        (): FormValues => ({
            ...omit(['id', 'paymentModuleId'], paymentSettings),
        }),
        [paymentSettings]
    );

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('adyenPaymentModuleDetails:paymentSettingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateAdyenPaymentSettingsMutation, UpdateAdyenPaymentSettingsMutationVariables>({
                    mutation: UpdateAdyenPaymentSettingsDocument,
                    variables: { moduleId, id: paymentSettingsId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('adyenPaymentModuleDetails:paymentSettingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, moduleId, paymentSettingsId, navigate]
    );

    const environmentOptions = useEnvironmentOptions();
    const validate = useValidator(validator);

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="paymentSettingsForm" htmlType="submit" type="primary">
                    {t('adyenPaymentModuleDetails:paymentSettingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('adyenPaymentModuleDetails:paymentSettingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="paymentSettingsForm"
                        layout="vertical"
                        name="paymentSettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card title={t('adyenPaymentModuleDetails:dividers.mainDetails')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t(
                                                'adyenPaymentModuleDetails:paymentSettingsDrawer.fields.displayName',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="displayName"
                                            required
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                                                returnObjects: true,
                                            })}
                                            name="currency"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputNumberField
                                            {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                                                returnObjects: true,
                                            })}
                                            name="amount"
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            <Card title={t('adyenPaymentModuleDetails:dividers.paymentSettings')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.merchant', {
                                                returnObjects: true,
                                            })}
                                            name="merchantAccount"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.apiKey', {
                                                returnObjects: true,
                                            })}
                                            name="apiKey"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.clientKey', {
                                                returnObjects: true,
                                            })}
                                            name="clientKey"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t(
                                                'adyenPaymentModuleDetails:paymentSettingsDrawer.fields.liveEndpointUrlPrefix',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="liveEndpointUrlPrefix"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <SelectField
                                            {...t(
                                                'adyenPaymentModuleDetails:paymentSettingsDrawer.fields.environment',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="environment"
                                            options={environmentOptions}
                                            showSearch
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            <Card title={t('adyenPaymentModuleDetails:dividers.notificationSettings')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <InputFieldArray
                                            {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.hmacKeys', {
                                                returnObjects: true,
                                            })}
                                            labelForAdd={t(
                                                'adyenPaymentModuleDetails:paymentSettingsDrawer.addHmacKeyButton'
                                            )}
                                            name="hmacKeys"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.username', {
                                                returnObjects: true,
                                            })}
                                            name="username"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <PasswordField
                                            {...t('adyenPaymentModuleDetails:paymentSettingsDrawer.fields.password', {
                                                returnObjects: true,
                                            })}
                                            name="password"
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Space>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdateAdyenPaymentSettingInnerPage;
